import React from 'react';
import PropTypes from 'prop-types';

function isBlank(val) {
  return val === null || val === undefined || val === '';
}

function formatAmount(amount) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '--';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
// Helper: Parse date string as local midnight if in YYYY-MM-DD format, and return
// formatted string
const parseLocalDate = (dateStr) => {
  if (!dateStr) return '';
  let dateObj;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    dateObj = new Date(`${dateStr}T00:00:00`);
  } else {
    dateObj = new Date(dateStr);
  }
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

function LendingCardSummary({
  metaData,
  realTimeBalance,
  planSummary,
  globalLimits,
}) {
  // Conditions
  const isRevolve = metaData?.GBL_API_PMC_paymentType === 'Revolve';
  const isConfig8C8 = metaData?.GBL_API_SPD_configCode === '8C8';
  const activePlans = planSummary?.activePlansCount > 0;
  const isSupp = metaData?.IDV_API_DemographicsLookUp_AccountRoleType === 'SUPP';
  const isCancelled = metaData?.IDV_API_DemographicsLookUp_AccountStatus === 'Cancelled';
  const suppLimit = globalLimits?.open_to_buy_limits?.supp_limit;
  const suppExposure = globalLimits?.open_to_buy_limits?.supp_exposure;
  // Find Domestic market group and get available credit
  const domesticMarketGroup = Array.isArray(globalLimits?.open_to_buy_limits?.market_group)
    ? globalLimits.open_to_buy_limits.market_group.find((mg) => mg.market_type === 'Domestic')
    : undefined;
  const availableCredit = domesticMarketGroup
    ?.transaction_limits?.total_monthy_limit?.available_funds;

  // Balances
  const balancesArr = Array.isArray(realTimeBalance?.balances) ? realTimeBalance.balances : [];
  const balanceSummary = balancesArr[0]?.balanceSummary || {};
  const paymentDueDate = balancesArr[0]?.paymentDueDate;

  // Display fields
  const fields = [];

  // Standard/Payment Terms/Last Statement Balance
  if (isConfig8C8) {
    fields.push({
      title: 'Standard Balance',
      value: formatAmount(balanceSummary?.deferrableBalance?.openingBalance?.amount),
    });
    fields.push({
      title: 'Payment Terms Balance',
      value: formatAmount(balanceSummary?.deferredBalance?.openingBalance?.amount),
    });
  } else {
    fields.push({
      title: 'Last Statement Balance',
      value: formatAmount(balanceSummary?.openingBalance?.amount),
    });
  }

  // Always show
  fields.push({
    title: 'Total Balance',
    value: formatAmount(balanceSummary?.closingBalance?.amount),
  });
  fields.push({
    title: 'Minimum Payment Due',
    value: formatAmount(balanceSummary?.paymentDueBalance?.amount),
  });
  fields.push({
    title: 'Due Date',
    value: paymentDueDate ? parseLocalDate(paymentDueDate) : '--',
  });

  // Past Due Amount
  const pastDueAmount = Number.parseFloat(balanceSummary?.pastPaymentDueBalance?.amount);
  if (pastDueAmount > 0) {
    fields.push({
      title: 'Past Due Amount',
      value: formatAmount(pastDueAmount),
    });
  }

  // Adjusted/Remaining Balances
  if (activePlans) {
    fields.push({
      title: 'Adjusted Balance',
      value: formatAmount(balanceSummary?.adjustedBalance?.amount),
    });
  } else if (isConfig8C8) {
    fields.push({
      title: 'Remaining Standard Balance',
      value: formatAmount(balanceSummary?.adjustedBalance?.amount),
    });
  } else {
    fields.push({
      title: 'Remaining Statement Balance',
      value: formatAmount(balanceSummary?.remainingStatementBalance?.amount),
    });
  }

  // SUPP Spending Limit
  if (
    isSupp
        && !isBlank(suppLimit)
        && !isBlank(suppExposure)
        && !isCancelled
  ) {
    fields.push({
      title: 'Your Spending Limit is',
      value: formatAmount(suppLimit),
    });
    fields.push({
      title: 'You have spent',
      value: formatAmount(suppExposure),
    });
  }

  // Available Credit (not SUPP)
  if (!isSupp && !isCancelled && availableCredit) {
    fields.push({
      title: 'Available Credit',
      value: formatAmount(availableCredit),
    });
  }

  // Only display if Lending card and paymentType is Revolve
  if (!isRevolve) return null;

  // Filter out fields with value '--'
  const visibleFields = fields.filter((field) => field.value !== '--');

  return (
    <div className="width-full">
      {visibleFields.map((field) => (
        <div key={field.title} className="flex flex-justify-between width-full">
          <p className="body-3 text-align-left dls-gray-05">{field.title}</p>
          <p className="body-3">{field.value}</p>
        </div>
      ))}
    </div>
  );
}

LendingCardSummary.propTypes = {
  metaData: PropTypes.shape({
    GBL_API_PMC_paymentType: PropTypes.string,
    GBL_API_SPD_configCode: PropTypes.string,
    IDV_API_DemographicsLookUp_AccountRoleType: PropTypes.string,
    IDV_API_DemographicsLookUp_AccountStatus: PropTypes.string,
  }).isRequired,
  realTimeBalance: PropTypes.shape({
    balances: PropTypes.shape({
      balanceSummary: PropTypes.shape({
        deferrableBalance: PropTypes.shape({
          openingBalance: PropTypes.shape({
            amount: PropTypes.number,
          }),
        }),
        deferredBalance: PropTypes.shape({
          openingBalance: PropTypes.shape({
            amount: PropTypes.number,
          }),
        }),
        openingBalance: PropTypes.shape({
          amount: PropTypes.number,
        }),
        closingBalance: PropTypes.shape({
          amount: PropTypes.number,
        }),
        paymentDueBalance: PropTypes.shape({
          amount: PropTypes.number,
        }),
        pastPaymentDueBalance: PropTypes.shape({
          amount: PropTypes.number,
        }),
        adjustedBalance: PropTypes.shape({
          amount: PropTypes.number,
        }),
        remainingStatementBalance: PropTypes.shape({
          amount: PropTypes.number,
        }),
      }),
      paymentDueDate: PropTypes.string,
    }),
  }).isRequired,
  planSummary: PropTypes.shape({
    activePlansCount: PropTypes.number,
  }).isRequired,
  globalLimits: PropTypes.shape({
    open_to_buy_limits: PropTypes.shape({
      supp_limit: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      supp_exposure: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      market_group: PropTypes.arrayOf(PropTypes.shape({
        market_type: PropTypes.string,
        merchant_limits: PropTypes.shape({}),
        transaction_limits: PropTypes.shape({
          declining_balance_monthly_limit: PropTypes.shape({}),
          total_monthy_limit: PropTypes.shape({
            available_funds: PropTypes.number,
          }),
          transaction_limit: PropTypes.shape({}),
        }),
        atm_limits: PropTypes.shape({
          cash_despensing_min: PropTypes.number,
          travellers_check_despensing_min: PropTypes.number,
        }),
      })),
    }),
  }).isRequired,
};

export default LendingCardSummary;
